import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, HardHat, Briefcase, Users } from "lucide-react";
interface OrgCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
  imageUrl?: string;
  className?: string;
}
function OrgCard({
  title,
  subtitle,
  icon: Icon,
  imageUrl,
  className
}: OrgCardProps) {
  return <Card className={`w-48 md:w-56 h-48 flex flex-col rounded-lg border-4 border-primary bg-white text-foreground shadow-none relative z-10 group hover:border-primary transition-colors ${className || ''}`}>
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary" />
      <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary" />

      <CardContent className="p-4 flex-1 flex flex-col items-center justify-center text-center gap-3">
        <div className="p-2 bg-background rounded-sm border border-border/50 text-primary shadow-sm">
          {imageUrl ? <img src={imageUrl} alt={title} className="w-5 h-5 object-contain" loading="lazy" /> : Icon && <Icon className="w-5 h-5" strokeWidth={1.5} />}
        </div>
        <div>
          <h3 className="font-sans font-bold text-sm text-foreground uppercase tracking-wide leading-tight">
            {title}
          </h3>
          {subtitle && <p className="text-[10px] text-muted-foreground font-mono mt-1 uppercase tracking-wider">
              {subtitle}
            </p>}
        </div>
      </CardContent>
    </Card>;
}
export default function OrganisationalStructure() {
  return <section id="organisationalstructure" className="bg-secondary text-secondary-foreground py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: `linear-gradient(to right, var(--color-primary) 1px, transparent 1px), linear-gradient(to bottom, var(--color-primary) 1px, transparent 1px)`,
      backgroundSize: "3rem 3rem",
      maskImage: "radial-gradient(circle at center, black, transparent 80%)",
      WebkitMaskImage: "radial-gradient(circle at center, black, transparent 80%)"
    }} />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 rounded-lg blur-3xl pointer-events-none" />

      

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-6 font-mono text-primary border-primary/30 bg-secondary/50 backdrop-blur-sm">
            BOTTOM UP PROJECTS 
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-sans font-extrabold uppercase tracking-widest text-secondary-foreground">
            Our Organisational<br />Structure
          </h2>
        </div>

        <div className="w-full overflow-x-auto pb-8 hide-scrollbar">
          <div className="min-w-[300px] max-w-4xl mx-auto relative pt-4">
            
            <div className="relative flex flex-col items-center w-full">
              
              <div data-index={0} className="relative flex flex-col items-center">
                <OrgCard title="Client" subtitle="Primary Stakeholder" icon={User} />
              </div>
              <div className="w-px h-8 bg-primary/60" />

              
              <div data-index={1} className="flex gap-4">
                <OrgCard title="Project Director" subtitle="Superintendent" icon={User} />
                <OrgCard title="Consultants" subtitle="External Experts" icon={Users} />
              </div>
              <div className="w-px h-8 bg-primary/60" />

              
              <div data-index={2} className="relative flex flex-col items-center">
                <OrgCard title="(Bottom Up Projects Pty Ltd)" subtitle="HEAD CONTRACTOR" imageUrl="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/image_bfks744y.png" className="w-64 md:w-80 shadow-xl shadow-primary/40 bg-background/90 scale-110 ring-1 ring-primary/60" />
              </div>
              <div className="w-px h-8 bg-primary/60" />

              
              <div data-index={3} className="relative flex flex-col items-center">
                <OrgCard title="Construction Manager" subtitle="Operations" icon={HardHat} />
              </div>
              <div className="w-px h-8 bg-primary/60" />

              
              <div data-index={4} className="relative flex flex-col items-center">
                <OrgCard title="Project Manager" subtitle="Planning & Control" icon={Briefcase} />
                
                <div className="flex flex-col items-center w-full">
                  <div className="w-px h-6 bg-primary/60" />
                  <div className="flex gap-4 md:gap-8 items-start relative">
                    <div className="absolute w-full h-px bg-primary/60" style={{
                    top: '2.5rem',
                    left: 0,
                    right: 0
                  }} />
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-primary/60" />
                      <OrgCard title="Site Supervisor" subtitle="Field Management" icon={User} className="scale-110 h-48" />
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-primary/60" />
                      <OrgCard title="Subcontractors & Suppliers" subtitle="Execution Team" icon={Users} className="scale-110" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="lg:hidden flex justify-center gap-8 mt-12 border-t border-primary/20 pt-8">
          <Link to="/ContactPage" className="font-mono text-xs text-primary/80 hover:text-primary tracking-widest uppercase">
            [ SYS.SUPPLIERS ]
          </Link>
          
        </div>
      </div>
    </section>;
}